import { inject, Injectable } from '@angular/core'
import { Observable, Subject, Subscription, finalize } from 'rxjs'
import { UserService } from '../../users/services/user.service'
import {
  InterventionsService,
  BatchSlotResult,
} from '../../interventions/services/interventions.service'
import { UserModel } from '../../../core/models/user.model'
import { AvailabilityTools } from '../../../core/tools/availability-tools'
import { DateTools } from '../../../core/tools/date-tools'

/**
 * Empty day slot information
 */
export interface EmptyDaySlot {
  date: string
  dayLabel: string
  slots: Array<{ start: string; end: string }>
  totalAvailableMinutes: number
}

/**
 * Technician with route distance information
 */
export interface TechnicianWithDistance {
  technician: UserModel
  distance_km: number
  duration_min: number | null
  distance_type: 'route' | 'haversine'
}

/**
 * Service responsible for fetching and managing availability data
 */
@Injectable({
  providedIn: 'root',
})
export class AvailabilityDataService {
  private usersService = inject(UserService)
  private interventionsService = inject(InterventionsService)

  // Subscriptions for cleanup
  private emptyDays$ = new Subscription()
  private closestTechnicians$ = new Subscription()
  private closestTechniciansByRoute$ = new Subscription()

  // Data storage
  public selectedInterventionAvailabilities: any = null
  public emptyDaysSlots: EmptyDaySlot[] = []
  public closestTechnicians: UserModel[] = []
  public closestTechniciansByRoute: TechnicianWithDistance[] = []

  // Loading states
  public loadingAvailabilities = false
  public loadingEmptyDays = false
  public loadingClosestTechnicians = false
  public loadingTechniciansByRoute = false

  /**
   * Cleanup subscriptions
   */
  destroy(): void {
    this.emptyDays$.unsubscribe()
    this.closestTechnicians$.unsubscribe()
    this.closestTechniciansByRoute$.unsubscribe()
  }

  /**
   * Clear all data
   */
  clearAll(): void {
    this.selectedInterventionAvailabilities = null
    this.emptyDaysSlots = []
  }

  /**
   * Fetch availabilities for a technician
   * @param technician The technician
   * @param dateRange The date range [start, end]
   * @param interventionDuration Duration of the intervention in minutes
   * @returns Observable that emits the availabilities
   */
  fetchAvailabilitiesForTechnician(
    technician: UserModel,
    dateRange: [Date, Date],
    interventionDuration: number
  ): Observable<any> {
    const result$ = new Subject<any>()

    const startOfRange = new Date(dateRange[0])
    startOfRange.setHours(8, 0, 0, 0)
    const endOfRange = new Date(dateRange[1])
    endOfRange.setHours(18, 0, 0, 0)

    const startDate = DateTools.formatDateTimeString(startOfRange, '08:00:00')
    const endDate = DateTools.formatDateTimeString(endOfRange, '18:00:00')

    const requestData = {
      start_date: startDate,
      end_date: endDate,
      duration: interventionDuration,
      user_ids: [technician.id],
    }

    this.loadingAvailabilities = true

    this.usersService
      .getAvailabilities(requestData)
      .pipe(finalize(() => (this.loadingAvailabilities = false)))
      .subscribe({
        next: (availabilities) => {
          if (availabilities.length > 0) {
            const rawAvailabilities = availabilities[0]

            // Split slots into intervention duration chunks
            const rawSlots =
              rawAvailabilities.availabilities?.available_slots ||
              rawAvailabilities.daily_availabilities?.flatMap(
                (day: any) => day.available_slots || []
              ) ||
              []

            const chunkedSlots = AvailabilityTools.splitSlotsIntoDurationChunks(
              rawSlots,
              interventionDuration
            )

            // Update availabilities with chunked slots
            this.selectedInterventionAvailabilities = {
              ...rawAvailabilities,
              availabilities: {
                ...rawAvailabilities.availabilities,
                available_slots: chunkedSlots,
              },
            }

            result$.next(this.selectedInterventionAvailabilities)
          } else {
            this.selectedInterventionAvailabilities = null
            result$.next(null)
          }
          result$.complete()
        },
        error: (error) => {
          console.error('Error fetching technician availabilities:', error)
          this.selectedInterventionAvailabilities = null
          result$.error(error)
        },
      })

    return result$.asObservable()
  }

  /**
   * Fetch empty days (journées creuses) for a technician
   * @param technicianId The technician's user ID
   * @param dateRange The date range [start, end]
   * @param interventionDuration Duration of the intervention in minutes
   * @returns Observable that emits empty days
   */
  fetchTechnicianEmptyDays(
    technicianId: number,
    dateRange: [Date, Date],
    interventionDuration: number
  ): Observable<EmptyDaySlot[]> {
    const result$ = new Subject<EmptyDaySlot[]>()

    // Cancel previous request
    this.emptyDays$.unsubscribe()

    const startOfRange = new Date(dateRange[0])
    startOfRange.setHours(8, 0, 0, 0)
    const endOfRange = new Date(dateRange[1])
    endOfRange.setHours(18, 0, 0, 0)

    const startDate = DateTools.formatDateTimeString(startOfRange, '08:00:00')
    const endDate = DateTools.formatDateTimeString(endOfRange, '18:00:00')

    const requestData = {
      start_date: startDate,
      end_date: endDate,
      duration: interventionDuration,
      user_ids: [technicianId],
      group_by_day: true,
    }

    this.loadingEmptyDays = true

    this.emptyDays$ = this.usersService
      .getAvailabilities(requestData)
      .pipe(finalize(() => (this.loadingEmptyDays = false)))
      .subscribe({
        next: (availabilities) => {
          if (
            availabilities.length > 0 &&
            availabilities[0].daily_availabilities
          ) {
            const dailyData = availabilities[0].daily_availabilities
            const emptyDays = this.extractEmptyDays(dailyData)
            this.emptyDaysSlots = emptyDays
            result$.next(emptyDays)
          } else {
            this.emptyDaysSlots = []
            result$.next([])
          }
          result$.complete()
        },
        error: (error) => {
          console.error('Error fetching empty days:', error)
          this.emptyDaysSlots = []
          result$.error(error)
        },
      })

    return result$.asObservable()
  }

  /**
   * Extract empty days from daily availabilities data
   */
  private extractEmptyDays(
    dailyData: Record<
      string,
      {
        available_slots: Array<{ start: string; end: string }>
        busy_slots: Array<{
          start: string
          end: string
          title: string
          type: string
        }>
      }
    >
  ): EmptyDaySlot[] {
    const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']
    const emptyDays: EmptyDaySlot[] = []

    for (const [dateStr, dayAvailabilities] of Object.entries(dailyData)) {
      const busySlots = dayAvailabilities.busy_slots || []
      const availableSlots = dayAvailabilities.available_slots || []

      // A day is "empty" if it has no busy slots and has available slots
      if (busySlots.length === 0 && availableSlots.length > 0) {
        const dateObj = new Date(dateStr + 'T00:00:00')
        const dayName = dayNames[dateObj.getDay()]
        const dd = String(dateObj.getDate()).padStart(2, '0')
        const mm = String(dateObj.getMonth() + 1).padStart(2, '0')

        const totalMinutes = availableSlots.reduce((sum, slot) => {
          return sum + AvailabilityTools.getSlotDurationMinutes(slot)
        }, 0)

        emptyDays.push({
          date: dateStr,
          dayLabel: `${dayName} ${dd}/${mm}`,
          slots: availableSlots,
          totalAvailableMinutes: totalMinutes,
        })
      }
    }

    // Sort by date
    emptyDays.sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    )

    return emptyDays
  }

  /**
   * Fetch closest technicians by department
   * @param departmentCode Department code
   * @param productsNames Product names to filter
   * @returns Observable that emits technicians
   */
  fetchClosestTechnicians(
    departmentCode: string,
    productsNames: string[]
  ): Observable<UserModel[]> {
    const result$ = new Subject<UserModel[]>()

    if (!departmentCode) {
      result$.next([])
      result$.complete()
      return result$.asObservable()
    }

    this.closestTechnicians$.unsubscribe()

    this.loadingClosestTechnicians = true

    this.closestTechnicians$ = this.usersService
      .getClosest(departmentCode, productsNames)
      .pipe(finalize(() => (this.loadingClosestTechnicians = false)))
      .subscribe({
        next: (technicians) => {
          this.closestTechnicians = technicians
          result$.next(technicians)
          result$.complete()
        },
        error: (error) => {
          console.error('Error fetching closest technicians:', error)
          result$.error(error)
        },
      })

    return result$.asObservable()
  }

  /**
   * Fetch closest technicians by route distance (Valhalla)
   * @param workSiteLatitude Worksite latitude
   * @param workSiteLongitude Worksite longitude
   * @param productsNames Product names to filter
   * @param limit Maximum number of technicians
   * @param maxDistance Maximum distance in km
   * @returns Observable that emits technicians with distances
   */
  fetchClosestTechniciansByRoute(
    workSiteLatitude: number,
    workSiteLongitude: number,
    productsNames: string[],
    limit: number = 10,
    maxDistance: number = 100
  ): Observable<TechnicianWithDistance[]> {
    const result$ = new Subject<TechnicianWithDistance[]>()

    this.closestTechniciansByRoute$.unsubscribe()

    this.loadingTechniciansByRoute = true

    this.closestTechniciansByRoute$ = this.usersService
      .getClosestByRoute({
        workSiteLatitude,
        workSiteLongitude,
        number: limit,
        maxDistance,
        productsNames: productsNames.length > 0 ? productsNames : undefined,
      })
      .pipe(finalize(() => (this.loadingTechniciansByRoute = false)))
      .subscribe({
        next: (response) => {
          this.closestTechniciansByRoute = response.technicians
          result$.next(response.technicians)
          result$.complete()
        },
        error: (error) => {
          console.error('Error fetching technicians by route:', error)
          result$.error(error)
        },
      })

    return result$.asObservable()
  }

  /**
   * Fetch selected intervention availabilities using single availability with travel info
   * @param intervention The selected intervention
   * @param affair The affair to schedule
   * @param dateRange The date range [start, end]
   * @param interventionDuration Duration in minutes
   * @param users All users for resolving technician
   * @returns Observable with availabilities
   */
  fetchSelectedInterventionAvailabilities(
    intervention: any,
    affair: { workSiteLatitude: number; workSiteLongitude: number },
    dateRange: [Date, Date],
    interventionDuration: number,
    users: any[]
  ): Observable<any> {
    const result$ = new Subject<any>()

    if (
      !intervention?.usersTechnicians ||
      intervention.usersTechnicians.length === 0
    ) {
      this.selectedInterventionAvailabilities = null
      result$.next(null)
      result$.complete()
      return result$.asObservable()
    }

    const techFromIntervention = intervention.usersTechnicians[0]
    const matchedUser = users.find(
      (u: any) => u.email === techFromIntervention.email
    )

    if (!matchedUser) {
      this.selectedInterventionAvailabilities = null
      result$.next(null)
      result$.complete()
      return result$.asObservable()
    }

    this.loadingAvailabilities = true

    this.interventionsService
      .singleAvailabilityWithTravel({
        affair: {
          workSiteLatitude: affair.workSiteLatitude,
          workSiteLongitude: affair.workSiteLongitude,
        },
        intervention: {
          id: intervention.id,
          technician_id: matchedUser.id,
          startDate: intervention.startDate,
          workSiteLatitude: intervention.affair?.workSiteLatitude,
          workSiteLongitude: intervention.affair?.workSiteLongitude,
          usersTechnicians: intervention.usersTechnicians,
        },
        duration: interventionDuration,
      })
      .pipe(finalize(() => (this.loadingAvailabilities = false)))
      .subscribe({
        next: (response: {
          status: string
          slots: BatchSlotResult[]
          busy_slots?: any[]
          date: string
        }) => {
          if (response.status === 'success' && response.slots) {
            // Format slots for display
            const formattedSlots = response.slots.map(
              (slot: BatchSlotResult) => ({
                start: slot.start,
                end: slot.end,
                available_minutes: slot.available_minutes,
                is_feasible: slot.is_feasible,
                needs_adjustment: slot.needs_adjustment,
              })
            )

            this.selectedInterventionAvailabilities = {
              technician: matchedUser,
              date: response.date,
              availabilities: {
                available_slots: formattedSlots,
                busy_slots: response.busy_slots || [],
              },
              rawSlots: response.slots,
            }

            result$.next({
              availabilities: this.selectedInterventionAvailabilities,
              rawSlots: response.slots,
            })
          } else {
            this.selectedInterventionAvailabilities = null
            result$.next(null)
          }
          result$.complete()
        },
        error: (error: Error) => {
          console.error('Error fetching intervention availabilities:', error)
          this.selectedInterventionAvailabilities = null
          result$.error(error)
        },
      })

    return result$.asObservable()
  }
}
